import { ArticleAction } from '../types';
import { i18n } from '@/plugins/i18n';
import { compareVersionNumbers } from '@/vendor/pleased-sdk/util';
import { platform, AllPlatforms } from '@/common/bridge';
import { store } from '@/store';
import { UserState } from '@/store/user';

const SUPPORTED_VERSIONS = {
  MIN_IOS: '1.98.0',
  MIN_APP: '1.21.81',
};

/**
 * These actions take to a specific page/screen/form
 * in application.
 */
export const PLEASED_ARTICLE_ACTIONS = {
  GO_DEPOSIT: 'GO_DEPOSIT',
  GO_WITHDRAW: 'GO_WITHDRAW',
  GO_BET_LIVE: 'GO_BET_LIVE',
  GO_BET_FOOTBALL_LIVE: 'GO_BET_FOOTBALL_LIVE',
  GO_TRANSACTIONS: 'GO_TRANSACTIONS',
  GO_KYC_VERIFICATION: 'GO_KYC_VERIFICATION',
  GO_NAME_UPDATE: 'GO_NAME_UPDATE',

  // To be supported
  GO_ACCOUNT_SETTINGS: 'GO_ACCOUNT_SETTINGS',
  GO_PROFILE_PAGE: 'GO_PROFILE_PAGE',
  GO_REGISTER: 'GO_REGISTER',
  GO_SPORTY_PIN: 'GO_SPORTY_PIN',
} as const;

type CompiledPlatformSupport = Partial<Record<AllPlatforms, (version: string) => boolean>>
interface VersionSupport {
  supports?: CompiledPlatformSupport
}

const DEFAULT_ACTION_VERSION_SUPPORT: CompiledPlatformSupport = {
  app: version => compareVersionNumbers(version, SUPPORTED_VERSIONS.MIN_APP) >= 0,
  ios: version => compareVersionNumbers(version, SUPPORTED_VERSIONS.MIN_IOS) >= 0,
  web: () => true,
  wap: () => true,
};

const make = (x: ArticleAction & VersionSupport) => ({ ...x,
  supports: {
    ...DEFAULT_ACTION_VERSION_SUPPORT,
    ...x.supports,
  } as VersionSupport['supports'] } as ArticleAction & VersionSupport);

const t = (key: string) => i18n.t(key) as string;

const ARTICLE_ACTIONS = {
  [PLEASED_ARTICLE_ACTIONS.GO_DEPOSIT]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_DEPOSIT,
    actionMessage: t('actions.go_deposit.action_message') as string,
    actionTitle: t('actions.go_deposit.action_title') as string,
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_WITHDRAW]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_WITHDRAW,
    actionMessage: t('actions.go_withdrawal.action_message'),
    actionTitle: t('actions.go_withdrawal.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_BET_LIVE]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_BET_LIVE,
    actionMessage: t('actions.go_bet_live.action_message'),
    actionTitle: t('actions.go_bet_live.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_BET_FOOTBALL_LIVE]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_BET_LIVE,
    actionMessage: t('actions.go_bet_football_live.action_message'),
    actionTitle: t('actions.go_bet_live.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_ACCOUNT_SETTINGS]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_ACCOUNT_SETTINGS,
    actionMessage: t('actions.go_account_settings.action_message'),
    actionTitle: t('actions.go_account_settings.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_TRANSACTIONS]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_TRANSACTIONS,
    actionMessage: t('actions.go_transactions.action_message'),
    actionTitle: t('actions.go_transactions.action_message'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_KYC_VERIFICATION]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_KYC_VERIFICATION,
    actionMessage: t('actions.go_kyc_verification.action_message'),
    actionTitle: t('actions.go_kyc_verification.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_NAME_UPDATE]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_NAME_UPDATE,
    actionMessage: t('actions.go_name_update.action_message'),
    actionTitle: t('actions.go_name_update.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_PROFILE_PAGE]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_PROFILE_PAGE,
    actionMessage: t('actions.go_profile_page.action_message'),
    actionTitle: t('actions.go_profile_page.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_REGISTER]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_REGISTER,
    actionMessage: t('actions.go_register.action_message'),
    actionTitle: t('actions.go_register.action_title'),
  }),
  [PLEASED_ARTICLE_ACTIONS.GO_SPORTY_PIN]: () => make({
    action: PLEASED_ARTICLE_ACTIONS.GO_SPORTY_PIN,
    actionMessage: t('actions.go_sporty_pin.action_message'),
    actionTitle: t('actions.go_sporty_pin.action_title'),
  }),
};

export const StaticArticleActions = {
  ...ARTICLE_ACTIONS,
  DEPOSIT: ARTICLE_ACTIONS.GO_DEPOSIT,
  GO_TO_DEPOSIT: ARTICLE_ACTIONS.GO_DEPOSIT,
  GO_TO_WITHDRAW: ARTICLE_ACTIONS.GO_WITHDRAW,
  PLACE_FOOTBALL_BET: ARTICLE_ACTIONS.GO_BET_FOOTBALL_LIVE,
  PLACE_A_FOOTBALL_BET: ARTICLE_ACTIONS.GO_BET_FOOTBALL_LIVE,
  PLACE_A_BET: ARTICLE_ACTIONS.GO_BET_LIVE,
  PLACE_BET: ARTICLE_ACTIONS.GO_BET_LIVE,
  GO_TO_KYC: ARTICLE_ACTIONS.GO_KYC_VERIFICATION,
};

export function resolveArticleAction (action: Partial<ArticleAction>): ArticleAction {
  const key = action?.action! as keyof typeof StaticArticleActions;
  if (!StaticArticleActions[key]) return action as ArticleAction;
  const staticAction = StaticArticleActions[key]();
  return {
    action: (action.action || staticAction.action) as string,
    actionMessage: action.actionMessage || staticAction.actionMessage,
    actionTitle: action.actionTitle || staticAction.actionTitle,
  };
}

export function checkActionSupported (action?: Partial<ArticleAction>): boolean {
  const key = action?.action! as keyof typeof StaticArticleActions;
  if (!StaticArticleActions[key]) {
    // This is a new action, we will check the default support for this action
    // By default, new action will only be supported on WEB/WAP, to avoid showing incorrect
    // actions that might not be supported by the platform.
    return platform === 'web' || platform === 'wap';
  }
  const staticAction = StaticArticleActions[key]();
  // If the action is defined, check platform.
  const vuexState = store.state as { user: UserState };
  // If we are not able to find the platform, it's safer to disable the action
  return Boolean(staticAction.supports?.[platform as AllPlatforms]?.(vuexState.user.customerAppVersion || ''));
}
